import { ReactNode, useContext } from 'react';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import dayjs from 'dayjs';
import { Badge } from 'src/components/ui';
import UWEFormContext from '../../../context/UWEFormContext';
import { getReportMetadata, isAmendmentEntity } from '../OcifBulkUpload/util';
import { useReportSchedules } from '../OcifBulkUpload/useReportSchedules';
import { daysUntil } from '../OcifBulkUpload/FilingStatusCard';

function InfoRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 py-1 border-b border-line last:border-b-0">
      <dt className="text-ink-3 small">{label}</dt>
      <dd className="mb-0 text-right small font-semibold text-ink">{children}</dd>
    </div>
  );
}

function DueIn({ days }: { days: number }) {
  const daysOverdue = -days;
  if (days < 0)
    return (
      <span className="text-danger">
        <Trans>{daysOverdue} days overdue</Trans>
      </span>
    );
  if (days === 0)
    return (
      <span className="text-warning">
        <Trans>due today</Trans>
      </span>
    );
  return (
    <span className="text-ink-3">
      <Trans>in {days} days</Trans>
    </span>
  );
}

/** Who filed the report, for which period, and where it stands. */
export function ReportInfoCard() {
  const { i18n } = useLingui();
  const currentEntity = useContext(UWEFormContext)?.currentEntity;
  const reportMetadata = getReportMetadata(currentEntity);
  const { scheduleTitles, inventoryScheduleNames } = useReportSchedules(currentEntity);

  const formatDate = (date?: string) =>
    date
      ? dayjs(date)
          .locale(i18n.locale)
          .format('ll')
      : '—';
  const dueDate: string | undefined = reportMetadata?.dueDate;
  const dueInDays = daysUntil(dueDate);
  const currentStep = currentEntity?.status?.[0];
  const financialCount = scheduleTitles.length;
  const inventoryCount = inventoryScheduleNames.length;

  return (
    <div className="rounded-lg border border-line bg-card p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="font-bold">
          <Trans>Report</Trans>
        </span>
        {isAmendmentEntity(currentEntity) && (
          <Badge color="warning" pill>
            <Trans>Amendment</Trans>
          </Badge>
        )}
      </div>
      <dl className="mb-0">
        <InfoRow label={<Trans>Institution</Trans>}>{currentEntity?.organizationalUnit?.name ?? '—'}</InfoRow>
        <InfoRow label={<Trans>Period</Trans>}>{reportMetadata?.reportingWindowPeriod ?? reportMetadata?.periodIdentifier ?? '—'}</InfoRow>
        <InfoRow label={<Trans>Due date</Trans>}>
          {formatDate(dueDate)}
          {dueInDays !== null && (
            <>
              {' · '}
              <DueIn days={dueInDays} />
            </>
          )}
        </InfoRow>
        <InfoRow label={<Trans>Schedules</Trans>}>
          <Trans>
            {financialCount} financial · {inventoryCount} inventory
          </Trans>
        </InfoRow>
        <InfoRow label={<Trans>Created date</Trans>}>{formatDate(currentEntity?.createdAt)}</InfoRow>
        <InfoRow label={<Trans>Last updated</Trans>}>{formatDate(currentEntity?.lastUpdated)}</InfoRow>
      </dl>
    </div>
  );
}
